/**
 * Today. The rationale line is why an adaptive app doesn't feel arbitrary.
 *
 * The bottom button never blocks on a network call in the common path: if the
 * next session is already planned it starts instantly; if planning is still
 * running it says so and the learner can leave (docs/01).
 */

import { api } from "../api.ts";
import { cycleStrip, el, gloss, mount, percent, primaryButton, ruleTil, shell, statTile, bar } from "../ui.ts";
import type { AppState } from "../types.ts";

export interface TodayHandlers {
	openSession: (id: number) => void;
	openProgress: () => void;
	openSettings: () => void;
	refresh: () => void;
}

const WEEKDAYS_PT = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const MONTHS_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

export function renderToday(state: AppState, handlers: TodayHandlers, offline: boolean): void {
	const session = state.active_session ?? state.next_session;
	const body: Node[] = [];

	if (offline) {
		body.push(el("div", { class: "notice", text: "Offline. Sessions you have opened still work; answers sync when you are back." }));
		body.push(el("div", { style: "height:16px" }));
	}

	if (session === null) {
		body.push(...emptyState(state));
	} else {
		const kindLabel = session.kind === "test" ? "Teste" : session.kind === "review" ? "Revisão" : "Sessão";
		body.push(el("div", { class: "meta", text: `${kindLabel} ${session.ordinal}` }));
		body.push(el("h1", { class: "display", style: "margin-top:7px", lang: "pt-BR", text: session.title_pt }));
		const titleGloss = gloss(session.title_en, state.english_ratio);
		if (titleGloss !== null) body.push(titleGloss);

		body.push(ruleTil(9));

		if (session.degraded) {
			body.push(
				el(
					"div",
					{ class: "notice" },
					`Session ${session.ordinal} is review-only — planning failed. `,
					el("button", {
						class: "link",
						type: "button",
						text: "Retry planning",
						onclick: () => {
							void api
								.retryPlan()
								.then(() => handlers.refresh())
								.catch(() => handlers.refresh());
						},
					}),
				),
			);
		} else if (session.rationale.trim() !== "") {
			body.push(
				el(
					"div",
					{ class: "card" },
					el("div", { class: "meta", style: "margin-bottom:7px", text: "Why this session" }),
					el("p", { class: "en en--ink", lang: "en", text: session.rationale }),
				),
			);
		}

		body.push(
			el(
				"div",
				{ class: "grid-2 stack--tight" },
				statTile("Length", `${session.estimated_minutes} min`),
				statTile("Blocks", String(session.block_count)),
			),
		);
	}

	body.push(
		el(
			"div",
			{ class: "stack" },
			el("div", {
				class: "meta",
				text: `Cycle · review at ${state.cycle.next_review_at}, test at ${state.cycle.next_test_at}`,
			}),
			cycleStrip(state.cycle),
		),
	);

	body.push(
		el(
			"div",
			{ class: "stack" },
			el("div", { class: "meta", text: `Immersion · ${percent(1 - state.english_ratio)} português` }),
			bar(1 - state.english_ratio),
		),
	);

	body.push(
		el(
			"div",
			{ class: "link-row" },
			el("button", { class: "link", type: "button", text: "Progress", onclick: handlers.openProgress }),
			el("button", { class: "link", type: "button", text: "Settings", onclick: handlers.openSettings }),
		),
	);

	mount(
		shell({
			eyebrowLeft: todayLabel(),
			eyebrowRight: `${state.due_count} due`,
			body,
			footer: footerAction(state, handlers),
		}),
	);
}

function footerAction(state: AppState, handlers: TodayHandlers): Node {
	const session = state.active_session ?? state.next_session;

	if (session !== null) {
		const label = state.active_session === null ? `Start session ${session.ordinal}` : `Resume session ${session.ordinal}`;
		return primaryButton(label, () => handlers.openSession(session.id), {
			variant: session.kind === "test" ? "ochre" : undefined,
		});
	}

	if (state.planning) {
		const wrapper = el("div");
		wrapper.append(
			el(
				"p",
				{ class: "en center", style: "margin-bottom:9px" },
				el("span", { class: "spinner", "aria-hidden": "true" }),
				` Preparing session ${state.session_counter + 1}`,
			),
			primaryButton("Preparing…", () => {}, { disabled: true }),
		);
		return wrapper;
	}

	return primaryButton("Plan the next session", () => {
		void api
			.retryPlan()
			.then(() => handlers.refresh())
			.catch(() => handlers.refresh());
	});
}

function emptyState(state: AppState): Node[] {
	if (state.planning) {
		return [
			el("h1", { class: "display", style: "margin-top:20px", lang: "pt-BR", text: "Preparando" }),
			el("p", {
				class: "en",
				style: "margin-top:8px",
				text: `Session ${state.session_counter + 1} is being planned. You can close the app — it will be ready next time you open it.`,
			}),
		];
	}

	return [
		el("h1", { class: "display", style: "margin-top:20px", lang: "pt-BR", text: "Tudo em dia" }),
		el("p", {
			class: "en",
			style: "margin-top:8px",
			text:
				state.session_counter === 0
					? "No session yet. Plan the first one when you are ready."
					: `${state.session_counter} sessions done. Plan the next one when you are ready.`,
		}),
	];
}

/** Date in Portuguese, matching the eyebrow in the design. No clock. */
function todayLabel(): string {
	const now = new Date();
	return `${WEEKDAYS_PT[now.getDay()]} · ${now.getDate()} ${MONTHS_PT[now.getMonth()]}`;
}
